import { useEffect } from "react";

import PropTypes from "prop-types";

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Grid, Button } from "@mui/material";

import LoaderWrapper from "../../components/wrapper/LoaderWrapper";

import { useForm } from "../../hooks/useForm";

import useFilterSearchParams from "../../hooks/useFilterSearchParams";

import LeadChartFilterForm from "./LeadChartFilterForm";

import useDialog from "../../hooks/useDialog";
import UserFilterDialog from "../../components/dialog/UserFilterDialog";

import {
  getSearchParamsObjectForFilterPrefix,
  getFilterSearchParamsKeyForField,
  isEmptyArray,
  isEmptyValue,
} from "../../helpers/methods";

import FilterAltIcon from "@mui/icons-material/FilterAlt";

import {
  overflowButtonStyle,
  centerVericalAlignIconStyle,
} from "../../helpers/styles";
import { useLeadsChartFilteringData } from "../../services/leadsService";
import InfoButton from "../../components/button/InfoButton/InfoButton";

import { useDialogBetter } from "../../hooks/useDialogBetter";
import LegendDialog from "../../components/dialog/LegendDialog/LegendDialog";

const UserFilteringDialogForm = (props) => {
  return (
      <LeadChartFilterForm
        {...props}
        onlyFormContent={false}
        isVerticalForm={true}
        filteringData={props.filteringData}
        formValue={props.formValue}
      />
  );
};

export default function LeadChartToolBar(props) {
  const { t } = useTranslation();

  const [
    openUserFiltersDialog,
    onOpenUserFiltersDialog,
    onCloseUserFiltersDialog,
  ] = useDialog();

  const { data: filteringData } = useLeadsChartFilteringData()

  const [searchParams, setSearchParams] = useSearchParams();
  const { getSearchParamsByFilterPrefix, clearSearchParamsByFilterPrefix } =
    useFilterSearchParams(searchParams);

  const {
    formValue,
    setFormValue,
    onChangeDateConvertedToDateTimeWithEndHourOfDate,
    onChangeDateConvertedToDateTimeWithZeroHourOfDate,
    onChangeDate,
    onChange,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm(
    getSearchParamsByFilterPrefix(props.filterPrefix)
  );

  useEffect(() => {
    setFormValue(
      getSearchParamsByFilterPrefix(props.filterPrefix)
    );
  }, [searchParams]);

  const handleSearch = () => {
    setSearchParams(() => ({
      ...clearSearchParamsByFilterPrefix(props.filterPrefix),
      ...getSearchParamsObjectForFilterPrefix(formValue, props.filterPrefix),
      [getFilterSearchParamsKeyForField("page", props.filterPrefix)]: 1,
    }));
  };
  const handleCleanFilters = () => {
    setFormValue(() => ({}));
    setSearchParams(() => clearSearchParamsByFilterPrefix(props.filterPrefix));
  };

  const isLoading = formValue === undefined || !filteringData;

  const isFormValid = () => {
    let requiredFields = [];

    return requiredFields.every((field) =>
      Array.isArray(formValue[field])
        ? !isEmptyArray(formValue[field])
        : !isEmptyValue(formValue[field])
    );
  };

    const { openDialog: openLegendDialog } = useDialogBetter();
  
    const handleOpenLegendDialog = (e) => {
      openLegendDialog({
        Component: LegendDialog,
        componentProps: {
          tableData: props.legendDialogTableData,
        },
      });
    };

  return (
    <LoaderWrapper showLoader={isLoading}>
      <Grid
        container
        direction="row"
        justifyContent={props.showLegendButton ? "space-between" :"flex-end"}
        alignItems="center"
        columnSpacing={3}
        rowSpacing={2}
        marginY={1}
      >
        {props.showMyFiltersButton && (
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<FilterAltIcon sx={centerVericalAlignIconStyle} />}
              onClick={onOpenUserFiltersDialog}
              color={"primary"}
              sx={overflowButtonStyle}
            >
              {t("bar.tool_bar.my_filters")}
            </Button>
          </Grid>
        )}
        <LeadChartFilterForm
          onlyFormContent={true}
          formValue={formValue}
          setFormValue={setFormValue}
          onChange={onChange}
          onChangeDateConvertedToDateTimeWithEndHourOfDate={
            onChangeDateConvertedToDateTimeWithEndHourOfDate
          }
          onChangeDateConvertedToDateTimeWithZeroHourOfDate={
            onChangeDateConvertedToDateTimeWithZeroHourOfDate
          }
          onChangeDate={onChangeDate}
          onChangeAutocompleteFieldWithObjectOptions={
            onChangeAutocompleteFieldWithObjectOptions
          }
          isVerticalForm={false}
          filteringData={filteringData}
        />
        {props.showLegendButton &&<Grid
          item
          md={1.5}
        >
          <InfoButton 
            label={t("legend")}
            onClick={handleOpenLegendDialog}
          />
        </Grid>}
        <Grid
          container
          columnSpacing={2}
          item
          xs={12}
          md={props.showClearFiltersIcon ? 2.5 : 1.5}
          justifyContent={"flex-end"}
        >
          <Grid item xs={12} md={props.showClearFiltersIcon ? 5 : 12}>
            <Button
              variant="contained"
              color="success"
              size="small"
              fullWidth
              disabled={!isFormValid()}
              onClick={handleSearch}
            >
              {t("search")}
            </Button>
          </Grid>
          {props.showClearFiltersIcon && (
            <Grid item xs={12} md={7}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                onClick={handleCleanFilters}
              >
                {t("clean_filters")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      {openUserFiltersDialog && (
        <UserFilterDialog
          open={openUserFiltersDialog}
          onClose={onCloseUserFiltersDialog}
          pageName={props.pageName}
          filterPrefix={props.filterPrefix}
          filterForm={
            <UserFilteringDialogForm
              {...props}
              filteringData={filteringData}
            />
          }
        />
      )}
    </LoaderWrapper>
  );
}

LeadChartToolBar.propTypes = {
  pageName: PropTypes.string,
  filterPrefix: PropTypes.string,
  showMyFiltersButton: PropTypes.bool,
  showClearFiltersIcon: PropTypes.bool,
};

LeadChartToolBar.defaultProps = {
  showMyFiltersButton: true,
  showClearFiltersIcon: true,
};
